import { createSignal, type JSX } from 'solid-js';
import { t } from '../i18n';
import { setSpendCap } from '../lib/billing';
import { createMutation } from '../lib/mutation';
import { buttonClass } from './ui/Button';

// Inline editor for the account's monthly spend cap (#237) — the self-serve
// counterpart to the "reply for a different cap" alert email. Mock Billing():
// an `.input` + primary `.btn` row. On success it calls `onSaved` so the parent
// refetches usage (the panel shows the *effective* cap, which the server floors
// for new accounts, so a refetch is the source of truth — not the raw value
// typed here).
// Feedback goes through the parent's toast (#924), not `window.alert`: a native
// modal is off-design and blocks the page, and saving used to say NOTHING at all
// on success — the input kept the typed value whether or not it landed.
export function SpendCapEditor(props: {
  /** The cap in force, or null when the account has set no budget (#2057). */
  current: number | null;
  token: string | null;
  onSaved: () => void;
  /** Surface an outcome. Optional so the editor still renders standalone. */
  onNotice?: (message: string, tone: 'success' | 'error') => void;
}): JSX.Element {
  const [value, setValue] = createSignal(props.current === null ? '' : String(props.current));

  // The one guard that stays here: a cap of zero or a non-number is not a value
  // the form can send at all, so there is nothing for the server to arbitrate.
  // Anything the server COULD rule on (a cap under its floor, a plan that
  // forbids one) is left to it — `createMutation` carries no client validation.
  const save = createMutation({
    call: (cap: number) => setSpendCap(cap, props.token),
    fallback: t('billing.set_cap_error'),
    onSuccess: () => {
      props.onNotice?.(t('billing.cap_saved_toast'), 'success');
      props.onSaved();
    },
    onError: (message) => props.onNotice?.(message, 'error'),
  });

  const submit = (): void => {
    const cap = Number(value());
    if (!Number.isFinite(cap) || cap <= 0) {
      props.onNotice?.(t('billing.set_cap_error'), 'error');
      return;
    }
    void save.run(cap);
  };

  return (
    <form
      class="billing-cap"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <input
        class="input"
        type="number"
        min="1"
        step="1"
        aria-label={t('billing.set_cap_label')}
        value={value()}
        disabled={save.pending()}
        onInput={(e) => setValue(e.currentTarget.value)}
      />
      <button type="submit" class={buttonClass()} disabled={save.pending()}>
        {t('billing.set_cap_save')}
      </button>
    </form>
  );
}
